<template>
    <div
        :id="elementId"
        class="RichTextEditor"
        :class="{
            'RichTextEditor--disabled': disabled,
        }"
    >
        <div class="quill-editor">
            <div ref="editorContainer" />
        </div>
    </div>
</template>

<style lang="scss">
    @use "sass:color";

    @import "~quill/dist/quill.snow.css";
    @import "~quill/dist/quill.core.css";
    @import "~quill-mention/dist/quill.mention.min.css";

    // stylelint-disable selector-max-combinators, selector-max-compound-selectors, rule-empty-line-before
    .RichTextEditor {
        display: flex;

        .quill-editor {
            display: flex;
            flex-direction: column;
            width: 100%;

            .ql-editor {
                overflow: hidden;

                h1, h2, h3, h4, h5, h6 {
                    font-family: inherit;
                    font-weight: bold;
                    margin: 15px 0;
                }

                p {
                    margin: 10px 0;
                }

                .mention {
                    position: relative;
                    display: inline-block;
                    white-space: nowrap;
                    user-select: all;
                    padding: 1.5px 4px;
                    width: auto;
                    height: auto;

                    // Hack: ensure that copied element will not break background styles
                    background: rgba(#fff, 0.001);
                    &:before {
                        content: "";
                        position: absolute;
                        left: 0;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(#2ab496, 0.3);
                        pointer-events: none;
                    }
                    // End of hack

                    > span {
                        margin: 0;
                        border-radius: 0;
                    }
                }

                // Hack: allow simulating <br>s inside of the container
                // stylelint-disable-next-line selector-type-no-unknown
                sbr {
                    white-space: pre;
                    user-select: all;
                }
            }
        }

        &--disabled {
            filter: grayscale(1);
            opacity: 0.4;
        }
    }

    .ql-mention-list-container {
        max-height: 120px;
        border-color: color.mix(#2ab496, #fff, 15%);

        &::-webkit-scrollbar {
            width: 4px;
            background: #f5f5f5;
        }

        &::-webkit-scrollbar-thumb {
            background: #bbb;
        }
    }

    .ql-mention-list-item {
        font-size: 0.8rem;
        line-height: 1.5em;
        padding: 4px 7px;
        border-bottom: 1px solid color.mix(#2ab496, #fff, 15%);

        &.selected {
            background: color.mix(#2ab496, #fff, 50%);
        }

        &:before {
            content: "{{";
        }

        &:last-child {
            border-bottom: 0;
        }
    }
</style>

<script lang="ts">
    import Quill from 'quill';

    import 'quill-mention';
    import { markRaw } from 'vue';

    import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';

    import IndentStyle from './IndentAttributor';
    import SmartBreaker from './SmartBreaker';
    import { SearchItem } from './types';
    import {
        buildOutputHtml,
        buildQuillHtml,
        getMatchingVariables,
        onQuillUpdateReady,
        rememberQuillPosition,
    } from './utils';

    const AlignStyle = Quill.import('attributors/style/align');
    Quill.register(AlignStyle, true);
    Quill.register(IndentStyle, true);
    Quill.register('modules/smart-breaker', SmartBreaker);

    // PRODUCT-26110 Task 13 (Finding 1): this component used to wrap vue-quill-editor
    // (Vue-2-only, precompiled `_c` render fn -- crashes under MODE 3's PRIVATE_APIS).
    // vue-quill-editor was a thin wrapper (see its src/editor.vue, ~100 LOC) around a
    // plain `new Quill(el, options)` call plus a few event/watcher bridges; this
    // rewrite inlines exactly that wrapper's behavior directly against `quill`
    // (already a direct dependency) on a template-ref div, so no third-party Vue
    // integration layer is needed at all.
    @Component({
        emits: [ 'input', 'change', 'blur', 'focus' ],
    })
    export default class RichTextEditor extends Vue {
        @Prop({ type: String, default: 'Enter text here' })
        private placeholder!: string;

        @Prop({ type: String, default: '' })
        private value!: string;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Array, default: () => [] })
        private variables!: string[];

        @Prop({ type: Boolean })
        private allowLinks!: boolean;

        @Prop({ type: String, default: null })
        private id!: string | null;

        private uuid: string = `rt_${Math.round(Math.random() * 1e6)}_${Date.now()}`;
        private inputValue: string = this.value;

        // PRODUCT-26110 Task 13 (Finding 1): the last HTML content this component itself
        // pushed into (or read out of) Quill -- mirrors vue-quill-editor's own `_content`
        // bookkeeping, which existed purely to avoid re-`pasteHTML`-ing content Quill
        // already has (that would reset the undo history / caret for no reason).
        private lastContent: string = '';

        private quillInstance: Quill | null = null;

        @Ref()
        private readonly editorContainer!: HTMLDivElement;

        private get elementId(): string {
            return this.id || this.uuid;
        }

        private options: any = {
            debug: 'warn',
            modules: {
                'mention': {
                    allowedChars: /^[a-zA-Z0-9 _.:-]*}?$/,
                    mentionDenotationChars: [ '{{' ],
                    source: (query: string, render: (list: SearchItem[], query: string) => void) => {
                        render(getMatchingVariables(this.variables, query), query);
                    },
                },
                'toolbar': [
                    [ 'bold', 'italic', 'underline', 'strike', this.allowLinks ? 'link' : null ].filter(Boolean),
                    [ { header: 1 }, { header: 2 } ],
                    [ { list: 'ordered' }, { list: 'bullet' } ],
                    [ { indent: '-1' }, { indent: '+1' } ],
                    [ { header: [ 1, 2, 3, 4, 5, 6, false ] } ],
                    [ { color: [] }, { background: [] } ],
                    [ { align: [] } ],
                    [ 'clean' ],
                ],
                'smart-breaker': true,
                'clipboard': {
                    matchVisual: false,
                },
            },
            readOnly: false,
            theme: 'snow',
            bounds: `#${this.elementId}`,
            placeholder: '',
        };

        public getValue(): string {
            return (this.quillInstance?.getText() || this.value || '').trim();
        }

        // PRODUCT-26110 Task 13 (Finding 1): replicates vue-quill-editor's own `value`
        // watcher (editor.vue), which used to run as a *separate* component's reactivity
        // once `inputValue` propagated down as its `value` prop. Now that Quill is owned
        // directly by this component, the paste has to be triggered from here instead --
        // same guard (`newVal && newVal !== _content` -> pasteHTML; falsy -> setText('')),
        // and the position-retaining observer is still registered *before* the mutation,
        // exactly as before, so the MutationObserver in utils.ts still fires after it.
        @Watch('value', { immediate: true })
        @Watch('variables')
        private onValueChange(): void {
            const quill = this.quillInstance;
            const html = buildQuillHtml(this.value, this.variables).html;
            if (html === this.inputValue) {
                return;
            }
            this.inputValue = html;
            if (!quill) {
                return;
            }
            const retainPosition = rememberQuillPosition(quill);
            const unsubscribe = onQuillUpdateReady(quill, () => {
                retainPosition();
                unsubscribe();
            });
            if (html && html !== this.lastContent) {
                this.lastContent = html;
                quill.pasteHTML(html);
            } else if (!html) {
                this.lastContent = html;
                quill.setText('');
            }
        }

        public isEmpty(): boolean {
            return this.getValue() === '';
        }

        @Watch('placeholder', { immediate: true })
        private onPlaceholderChange(): void {
            this.options = { ...this.options, placeholder: this.placeholder };
            // @see: https://github.com/quilljs/quill/issues/1150
            if (this.quillInstance) {
                this.quillInstance.root.dataset.placeholder = this.options.placeholder;
            }
        }

        private onInput(value: string): void {
            const { html } = buildOutputHtml(value);
            if (html !== this.value) {
                this.$emit('input', html);
            }
        }

        private onChange(value: { quill: Quill, html: string, text: string }): void {
            const result = buildOutputHtml(value.html);
            if (result.html !== this.value) {
                this.$emit('change', { ...value, ...result });
            }
        }

        // PRODUCT-26110 Task 13 (Finding 1): inlines vue-quill-editor's `initialize()`
        // (editor.vue) directly against the template-ref div -- construct, paste initial
        // content while disabled (so the initial paste can't be perceived as a user edit),
        // re-enable per the `disabled` prop, then wire the same two listeners editor.vue
        // wired (`selection-change` -> blur/focus, `text-change` -> input/change).
        private mounted(): void {
            // Kept non-reactive: class fields become reactive data, and Quill run through
            // Vue proxies lets its internal model drift from the DOM, which crashes caret
            // restoration (IndexSizeError on Range.setStart) when merge variables change.
            const quill = markRaw(new Quill(this.editorContainer, this.options));
            this.quillInstance = quill;

            quill.enable(false);
            if (this.inputValue) {
                this.lastContent = this.inputValue;
                quill.pasteHTML(this.inputValue);
            }
            if (!this.disabled) {
                quill.enable(true);
            }

            quill.on('selection-change', (range) => {
                this.$emit(range ? 'focus' : 'blur', quill);
            });

            quill.on('text-change', () => {
                let html = quill.root.innerHTML;
                if (html === '<p><br></p>') {
                    html = '';
                }
                this.lastContent = html;
                this.onInput(html);
                this.onChange({ quill, html, text: quill.getText() });
            });

            // @ts-ignore: accessing Quill internals, to fix bug that Ctrl+Z is getting back to empty content
            quill.history.clear();
        }

        private unmounted(): void {
            this.quillInstance = null;
        }

        @Watch('disabled')
        private updateDisabilityStatus(): void {
            if (this.disabled) {
                this.quillInstance?.disable();
            } else {
                this.quillInstance?.enable();
            }
        }
    }
</script>
